// import

import clsx from 'clsx';
import {styled} from 'goober';
import * as R from 'ramda';
import type {PropsWithChildren} from 'react';
import {useMemo} from 'react';

import {Link} from 'modules/Link';
import {Type} from 'modules/Type';

// types

type Heading = {
  id: string;
  level: number;
  text: string;
  children?: Heading[];
};

type Limits = {min?: number; max?: number};

type Props = PropsWithChildren<{
  min?: number;
  max?: number;
  headings?: Heading[];
}>;

// fns

const pruners = [
  R.reject(R.propEq('level', 6)),
  R.reject(R.propEq('level', 5)),
  R.reject(R.propEq('level', 4)),
  R.reject(R.propEq('level', 3)),
  R.reject(R.propEq('level', 2)),
];

const pruneHeadings = (
  {min = 0, max = Infinity}: Limits,
  heads: Heading[],
) => {
  return R.reduce((res, fn) => {
    const out = fn(res);

    return out.length <= max ? R.reduced(out.length < min ? [] : out) :
      out.length < min ? R.reduced(res.length < min ? [] : res) : out;
  }, heads, pruners);
};

const deepPush = (last: Heading, next: Heading) =>
  R.over(
    R.lensPath(R.times(R.always('children'), next.level - last.level)),
    R.append(next),
    last,
  );

const linkReducer = (acc: Heading[], next: Heading) => {
  const last = R.last(acc);
  next = {...next, children: []};

  return last && last.level < next.level ?
    [...R.init(acc), deepPush(last, next)] :
    [...acc, next];
};

function mapLinks(links: Heading[]) {
  return links.length ? (
    <ol className="linkList">
      {links.map(({id, text, children = []}) => (
        <li key={id}>
          <Link
            className={clsx('link', 'red')}
            href={`#${id}`}
            tabIndex={-1}
          >
            {text}
          </Link>

          {mapLinks(children)}
        </li>
      ))}
    </ol>
  ) : null;
}

const parseHeadings = (
  limits: Limits,
  heads: Heading[],
) => {
  return mapLinks(pruneHeadings(limits, heads).reduce(linkReducer, []));
};

// hook

export function useTOC(headings: Heading[], {min, max, ...props}) {
  return useMemo(() => {
    const links = parseHeadings({min, max}, headings);

    return links ? (
      <TableOfContents {...props}>
        {links}
      </TableOfContents>
    ) : null;
  }, [headings, min, max]);
}

// component

export function TableOfContents(props: Props) {
  const {headings = [], max, min, children, ...rest} = props;
  const links = children || parseHeadings({min, max}, headings);

  if (!links) {
    return null;
  }

  return (
    <Styled {...rest}>
      <div className="ornament">
        <div className="b1" />
        <div className="b2" />
        <div className="b3" />
      </div>

      <div className="body">
        <div className="name">Table of Contents</div>
        <Type className="type">
          {links}
        </Type>
      </div>

      <div className="ornament flip">
        <div className="b1" />
        <div className="b2" />
        <div className="b3" />
      </div>
    </Styled>
  );
}

// styles

const Styled = styled('nav')`
  & .body {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 0;
  }

  & .name {
    margin-top: 0.5rem;
    font-weight: 500;
    text-align: center;
    font-size: 1.375rem;
    font-family: var(--font-head);
  }

  & .type {
    padding: 0 0.5rem 0.75rem 0;
    line-height: 1.5rem;

    & ol {
      font-size: 1rem;
    }
  }

  & .ornament {
    &.flip {
      transform: rotate(180deg);
    }

    & div,
    & div::before,
    & div::after {
      height: 0.75rem;
      box-sizing: content-box;
    }

    & div {
      position: relative;
    }

    & div::before,
    & div::after {
      content: "";
      display: block;
      position: absolute;
      border-style: solid;
      border-color: var(--blue);
      border-width: 0;
    }

    & div::before {
      border-width: 0.375rem 0 0 0.375rem;
      border-top-left-radius: 1rem;
    }

    & div::after {
      border-width: 0.375rem 0.375rem 0 0;
      border-top-right-radius: 1rem;
    }

    & .b1 {
      &::before,
      &::after {
        width: 25%;
      }

      &::before {
        left: 25%;
      }

      &::after {
        right: 25%;
      }
    }

    & .b2 {
      &::before,
      &::after {
        width: 12.5%;
      }

      &::before {
        left: 12.5%;
      }

      &::after {
        right: 12.5%;
      }
    }

    & .b3 {
      &::before,
      &::after {
        width: 12.5%;
      }

      &::before {
        left: 0;
      }

      &::after {
        right: 0;
      }
    }
  }
`;
